import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { maskCPF } from "@/lib/masks";

const LOGO = "https://customer-assets-4nw71qhi.emergentagent.net/job_academia-control-3/artifacts/hiofilsr_LOGO%20AMERICANFIT.png";
const inputCls = "bg-black border-neutral-700 text-white";

export default function PortalLogin() {
  const [cpf, setCpf] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [firstAccess, setFirstAccess] = useState(false);
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const q = params.get("cpf");
    if (q) {
      const digits = q.replace(/\D/g, "");
      if (digits.length === 11) setCpf(maskCPF(digits));
    }
  }, [params]);

  const enter = (data) => {
    localStorage.setItem("fp_portal_token", data.token);
    localStorage.setItem("fp_student", JSON.stringify(data.student));
    navigate(`/portal/${data.student.id}`);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (firstAccess) {
      if (password.length < 6) return toast.error("A senha precisa ter pelo menos 6 caracteres");
      if (password !== confirm) return toast.error("As senhas não conferem");
    }
    setBusy(true);
    try {
      const { data } = await api.post(firstAccess ? "/portal/first-access" : "/portal/login", { cpf, password });
      if (firstAccess) toast.success("Senha criada! Bem-vindo(a) ao Portal");
      enter(data);
    } catch (err) {
      toast.error(firstAccess ? (err.response?.data?.detail || "Não foi possível criar a senha") : "CPF ou senha inválidos");
    } finally { setBusy(false); }
  };

  const toggle = () => { setFirstAccess((v) => !v); setPassword(""); setConfirm(""); };

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center p-4">
      <Card className="w-full max-w-sm p-6 bg-neutral-900 border-neutral-800 text-white">
        <div className="text-center mb-6">
          <img src={LOGO} alt="American Fit" className="mx-auto max-w-[220px] mb-3"/>
          <h1 className="font-heading text-xl font-bold" data-testid="portal-login-title">{firstAccess ? "Primeiro acesso" : "Portal do Aluno"}</h1>
          <p className="text-slate-400 text-sm mt-1">{firstAccess ? "Digite seu CPF e crie sua senha" : "Acesse seus treinos"}</p>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-slate-300">CPF</Label>
            <Input value={cpf} onChange={(e)=>setCpf(maskCPF(e.target.value))} inputMode="numeric" className={inputCls} data-testid="portal-cpf-input"/>
          </div>
          <div>
            <Label className="text-slate-300">{firstAccess ? "Nova senha (mín. 6 caracteres)" : "Senha"}</Label>
            <Input type="password" value={password} onChange={(e)=>setPassword(e.target.value)} className={inputCls} data-testid="portal-password-input" autoFocus={cpf.length > 0}/>
          </div>
          {firstAccess && (
            <div>
              <Label className="text-slate-300">Confirme a senha</Label>
              <Input type="password" value={confirm} onChange={(e)=>setConfirm(e.target.value)} className={inputCls} data-testid="portal-password-confirm-input"/>
            </div>
          )}
          <Button type="submit" disabled={busy} className="w-full bg-rose-600 hover:bg-rose-700" data-testid="portal-login-button">{firstAccess ? "Criar senha e entrar" : "Entrar"}</Button>
        </form>
        <button type="button" onClick={toggle} className="w-full mt-4 text-sm text-rose-400 hover:text-rose-300 underline-offset-4 hover:underline" data-testid="portal-first-access-toggle">
          {firstAccess ? "Já tenho senha — entrar" : "Primeiro acesso? Criar minha senha"}
        </button>
      </Card>
    </div>
  );
}
